import React, { useState, useEffect } from 'react';
import { Card } from '../Card';
import PageContainer from '../layouts/PageContainer';
import { dataService } from '../../services/dataService';
import { School, Teacher, Student, Class, UserRole } from '../../data/types';

interface AdminDashboardScreenProps {
  userRole?: UserRole;
  schoolId?: string; // Only needed for SchoolAdmin role
}

const AdminDashboardScreen: React.FC<AdminDashboardScreenProps> = ({ 
  userRole = 'SchoolAdmin' as UserRole,
  schoolId = 'school-001' // Default school ID for demo purposes
}) => {
  const [schools, setSchools] = useState<School[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch data only for the specific school
        const [schoolsData, teachersData, studentsData, classesData] = await Promise.all([
          dataService.getSchools(),
          dataService.getTeachers(),
          dataService.getStudents(),
          dataService.getClasses()
        ]);
        
        // Filter data for the specific school
        const schoolData = schoolsData.filter(school => school.id === schoolId);
        const teacherData = teachersData.filter(teacher => teacher.school?.id === schoolId);
        const studentData = studentsData.filter(student => student.school?.id === schoolId);
        const classData = classesData.filter(cls => cls.schoolId === schoolId);
        
        setSchools(schoolData);
        setTeachers(teacherData);
        setStudents(studentData);
        setClasses(classData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userRole, schoolId]);

  // Calculate metrics
  const totalSchools = schools.length;
  const totalTeachers = teachers.length;
  const totalStudents = students.length;
  const totalClasses = classes.length;
  
  // Recent activity (mock data) - different for Super Admin vs School Admin
  const recentActivities = userRole === 'SchoolAdmin' ? [
    {
      id: 1,
      user: 'John Davis',
      action: 'created a new learning path',
      school: schools[0]?.name || 'Your School',
      timestamp: '2023-05-25T10:30:00'
    },
    {
      id: 2,
      user: 'Sarah Wilson',
      action: 'assigned learning paths to Grade 3',
      school: schools[0]?.name || 'Your School',
      timestamp: '2023-05-25T09:15:00'
    },
    {
      id: 3,
      user: 'Robert Taylor',
      action: 'updated class configuration',
      school: schools[0]?.name || 'Your School',
      timestamp: '2023-05-24T16:45:00'
    },
    {
      id: 4,
      user: 'Emma Martinez',
      action: 'reviewed student progress reports',
      school: schools[0]?.name || 'Your School',
      timestamp: '2023-05-24T14:30:00'
    },
  ] : [
    {
      id: 1,
      user: 'Jane Smith',
      action: 'modified school settings',
      school: 'Oakridge Elementary',
      timestamp: '2023-05-25T10:30:00'
    },
    {
      id: 2,
      user: 'Mike Johnson',
      action: 'added a new learning path',
      school: 'Pinecrest Middle School',
      timestamp: '2023-05-25T09:15:00'
    },
    {
      id: 3,
      user: 'Lisa Wong',
      action: 'uploaded new questions',
      school: 'Riverside Elementary',
      timestamp: '2023-05-24T16:45:00'
    },
    {
      id: 4,
      user: 'David Garcia',
      action: 'generated new reports',
      school: 'Lakeside K-8 Academy',
      timestamp: '2023-05-24T14:30:00'
    },
  ];

  // Format timestamp to readable date/time
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <PageContainer title={userRole === 'SchoolAdmin' ? 'School Dashboard' : 'Admin Dashboard'} userRole={userRole === 'SchoolAdmin' ? 'SchoolAdmin' : 'SuperAdmin'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={userRole === 'SchoolAdmin' ? 'School Dashboard' : 'Admin Dashboard'} userRole={userRole === 'SchoolAdmin' ? 'SchoolAdmin' : 'SuperAdmin'}>
      
      {/* Metrics - either district-wide or school-specific */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-white p-4 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">{userRole === 'SchoolAdmin' ? 'School' : 'Schools'}</p>
              <p className="text-2xl font-bold">{userRole === 'SchoolAdmin' ? (schools[0]?.name || 'Your School') : totalSchools}</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-white p-4 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 mr-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Teachers</p>
              <p className="text-2xl font-bold">{totalTeachers}</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-white p-4 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 mr-4">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Students</p>
              <p className="text-2xl font-bold">{totalStudents}</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-white p-4 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 mr-4">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Classes</p>
              <p className="text-2xl font-bold">{totalClasses}</p>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Activity Feed and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <Card className="bg-white p-4 shadow-sm">
            <h2 className="text-lg font-bold mb-4">Recent Activity</h2>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start p-3 border-b border-gray-100 last:border-b-0">
                  <div className="bg-gray-200 rounded-full p-2 mr-3">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-800">
                      <span className="font-medium">{activity.user}</span> {activity.action} at <span className="font-medium">{activity.school}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{formatTimestamp(activity.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <button className="text-primary hover:text-primary-dark text-sm font-medium">
                View All Activity
              </button>
            </div>
          </Card>
        </div>
        
        {/* Quick Actions */}
        <div>
          <Card className="bg-white p-4 shadow-sm mb-6">
            <h2 className="text-lg font-bold mb-4">Quick Actions</h2>
            <div className="space-y-2">

              <button 
                className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 flex items-center justify-between"
                onClick={() => window.location.href = '/admin/learning-paths'}
              >
                <span>Manage Learning Paths</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 flex items-center justify-between">
                <span>Manage Content</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 flex items-center justify-between">
                <span>School Settings</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 flex items-center justify-between">
                <span>Generate Reports</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Performance Sections - 2 column layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Column */}
          <div className="space-y-6">
            {/* School Performance */}
            <Card className="bg-white p-4 shadow-sm">
              <h2 className="text-lg font-bold mb-4">School Performance</h2>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm">Overall</span>
                  <span className="text-sm text-blue-600 font-bold">85%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm">Learning Path Completion</span>
                  <span className="text-sm text-green-600 font-bold">75%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '75%' }}></div>
                </div>
              </div>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm">Engagement Metrics</span>
                  <span className="text-sm text-purple-600 font-bold">65%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-purple-600 h-2.5 rounded-full" style={{ width: '65%' }}></div>
                </div>
              </div>
            </Card>
            
            {/* Teacher Performance */}
            <Card className="bg-white p-4 shadow-sm">
              <h2 className="text-lg font-bold mb-4">Teacher Performance</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-2">Teacher</th>
                      <th className="px-4 py-2">Avg Grade</th>
                      <th className="px-4 py-2">Top Class</th>
                      <th className="px-4 py-2">Classes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm">Jane Smith</td>
                      <td className="px-4 py-3 text-sm font-medium text-green-600">92.5%</td>
                      <td className="px-4 py-3 text-sm">Math 4A</td>
                      <td className="px-4 py-3 text-sm">3</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">John Drake</td>
                      <td className="px-4 py-3 text-sm font-medium text-green-600">87.0%</td>
                      <td className="px-4 py-3 text-sm">Science 5B</td>
                      <td className="px-4 py-3 text-sm">5</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Maria Williams</td>
                      <td className="px-4 py-3 text-sm font-medium text-green-600">85.0%</td>
                      <td className="px-4 py-3 text-sm">Math 3C</td>
                      <td className="px-4 py-3 text-sm">2</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Greg Rodriguez</td>
                      <td className="px-4 py-3 text-sm font-medium text-yellow-600">72.7%</td>
                      <td className="px-4 py-3 text-sm">Science 4D</td>
                      <td className="px-4 py-3 text-sm">4</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Sheila Black</td>
                      <td className="px-4 py-3 text-sm font-medium text-red-600">70.5%</td>
                      <td className="px-4 py-3 text-sm">Math 5A</td>
                      <td className="px-4 py-3 text-sm">3</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
          
          {/* Right Column */}
          <div className="space-y-6">
            {/* Class Performance */}
            <Card className="bg-white p-4 shadow-sm">
              <h2 className="text-lg font-bold mb-4">Class Performance</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-2">Class</th>
                      <th className="px-4 py-2">Avg</th>
                      <th className="px-4 py-2">Completion</th>
                      <th className="px-4 py-2">Top Path</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm">Math 3A</td>
                      <td className="px-4 py-3 text-sm">91.2%</td>
                      <td className="px-4 py-3 text-sm">94.8%</td>
                      <td className="px-4 py-3 text-sm">Algebra I</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Math 4B</td>
                      <td className="px-4 py-3 text-sm">88.5%</td>
                      <td className="px-4 py-3 text-sm">89.9%</td>
                      <td className="px-4 py-3 text-sm">Division</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Science 5A</td>
                      <td className="px-4 py-3 text-sm">86.3%</td>
                      <td className="px-4 py-3 text-sm">92.6%</td>
                      <td className="px-4 py-3 text-sm">Chemistry</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Math 5C</td>
                      <td className="px-4 py-3 text-sm">85.0%</td>
                      <td className="px-4 py-3 text-sm">81.2%</td>
                      <td className="px-4 py-3 text-sm">Fractions</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Science A</td>
                      <td className="px-4 py-3 text-sm">79.2%</td>
                      <td className="px-4 py-3 text-sm">72.4%</td>
                      <td className="px-4 py-3 text-sm">Plants</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
            
            {/* Student Performance */}
            <Card className="bg-white p-4 shadow-sm">
              <h2 className="text-lg font-bold mb-4">Student Performance</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-2">Student</th>
                      <th className="px-4 py-2">Avg</th>
                      <th className="px-4 py-2">Completion</th>
                      <th className="px-4 py-2">Class</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm">Emma Smith</td>
                      <td className="px-4 py-3 text-sm">93.0%</td>
                      <td className="px-4 py-3 text-sm">98.2%</td>
                      <td className="px-4 py-3 text-sm">Math 3A</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Ethan Garcia</td>
                      <td className="px-4 py-3 text-sm">92.0%</td>
                      <td className="px-4 py-3 text-sm">95.5%</td>
                      <td className="px-4 py-3 text-sm">Science 5A</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Mia Washington</td>
                      <td className="px-4 py-3 text-sm">90.0%</td>
                      <td className="px-4 py-3 text-sm">100.0%</td>
                      <td className="px-4 py-3 text-sm">Math 5C</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Aiden Thomas</td>
                      <td className="px-4 py-3 text-sm">82.7%</td>
                      <td className="px-4 py-3 text-sm">85.2%</td>
                      <td className="px-4 py-3 text-sm">Math 4B</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">Abigail Lee</td>
                      <td className="px-4 py-3 text-sm">75.0%</td>
                      <td className="px-4 py-3 text-sm">78.8%</td>
                      <td className="px-4 py-3 text-sm">Science A</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
            



          </div>
        </div>
      </PageContainer>
    );
  };

export default AdminDashboardScreen;